/**
 * Host Dashboard Component
 *
 * Main control panel for the quiz host with participant management,
 * question display, and game controls.
 */

import { useLiveQuizHost } from '../../../hooks/useLiveQuizHost';
import { CountdownTimer } from '../shared/CountdownTimer';
import { ConnectionStatus } from '../shared/ConnectionStatus';
import { Leaderboard } from '../shared/Leaderboard';
import { HostLobby } from './HostLobby';
import { QuestionController } from './QuestionController';
import { AnswerStatsChart } from './AnswerStatsChart';

interface HostDashboardProps {
  sessionId: string;
  hostUserId: string;
  onEnd?: () => void;
}

export function HostDashboard({ sessionId, hostUserId, onEnd }: HostDashboardProps) {
  const {
    connected,
    connecting,
    error,
    gamePin,
    status,
    settings,
    currentQuestionIndex,
    totalQuestions,
    currentQuestion,
    timeRemaining,
    participants,
    activeParticipants,
    readyCount,
    answerStats,
    leaderboard,
    answeredCount,
    correctAnswer,
    startGame,
    nextQuestion,
    revealAnswer,
    showLeaderboard,
    pauseGame,
    resumeGame,
    endGame,
    kickPlayer,
    skipQuestion,
    reconnect,
  } = useLiveQuizHost({ sessionId, hostUserId });

  const handleEndGame = () => {
    endGame();
    onEnd?.();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-indigo-900 to-blue-900">
      {/* Header */}
      <header className="bg-black/20 backdrop-blur-sm border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <h1 className="text-xl font-bold text-white">Live Quiz</h1>
            <ConnectionStatus
              connected={connected}
              connecting={connecting}
              error={error}
              onReconnect={reconnect}
            />
          </div>

          {gamePin && (
            <div className="flex items-center gap-6">
              <div className="text-right">
                <p className="text-xs text-purple-300 uppercase">Game PIN</p>
                <p className="text-2xl font-mono font-bold text-white tracking-wider">{gamePin}</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-purple-300 uppercase">Players</p>
                <p className="text-2xl font-bold text-white">{activeParticipants.length}</p>
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Lobby View */}
        {status === 'lobby' && (
          <HostLobby
            gamePin={gamePin || ''}
            participants={participants}
            readyCount={readyCount}
            onStart={startGame}
            onKick={kickPlayer}
            minPlayers={1}
          />
        )}

        {/* Starting Countdown */}
        {status === 'starting' && (
          <div className="flex flex-col items-center justify-center py-20">
            <h2 className="text-4xl font-bold text-white mb-8">Get Ready!</h2>
            <div className="text-8xl font-bold text-purple-400 animate-pulse">
              Starting...
            </div>
          </div>
        )}

        {/* Question View */}
        {(status === 'question' || status === 'reveal') && currentQuestion && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Question Area */}
            <div className="lg:col-span-2 space-y-6">
              <QuestionController
                question={currentQuestion}
                questionIndex={currentQuestionIndex}
                totalQuestions={totalQuestions}
                timeRemaining={timeRemaining}
                timeTotal={settings?.questionTimeSeconds || 20}
                status={status}
                answeredCount={answeredCount}
                totalParticipants={activeParticipants.length}
                correctAnswer={status === 'reveal' ? correctAnswer : undefined}
                onReveal={revealAnswer}
                onNext={nextQuestion}
                onSkip={skipQuestion}
              />

              {/* Answer Statistics */}
              {(status === 'question' || status === 'reveal') && answerStats && (
                <AnswerStatsChart
                  stats={answerStats}
                  options={currentQuestion.options || []}
                  correctAnswer={status === 'reveal' ? correctAnswer : undefined}
                />
              )}
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Timer */}
              {status === 'question' && (
                <div className="bg-white/5 rounded-xl p-6 text-center">
                  <CountdownTimer
                    seconds={timeRemaining}
                    totalSeconds={settings?.questionTimeSeconds || 20}
                    size="lg"
                  />
                </div>
              )}

              {/* Mini Leaderboard */}
              <div className="bg-white/5 rounded-xl p-4">
                <h3 className="text-sm font-semibold text-purple-300 uppercase tracking-wide mb-3">
                  Top Players
                </h3>
                <Leaderboard entries={leaderboard} maxEntries={5} compact />
              </div>

              {/* Participant List */}
              <div className="bg-white/5 rounded-xl p-4">
                <h3 className="text-sm font-semibold text-purple-300 uppercase tracking-wide mb-3">
                  Answers ({answeredCount}/{activeParticipants.length})
                </h3>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {activeParticipants.map((p) => {
                    const hasAnswered = answerStats?.totalAnswered
                      ? true
                      : false; // Simplified check

                    return (
                      <div
                        key={p.id}
                        className="flex items-center justify-between text-sm"
                      >
                        <span className="text-white truncate">{p.nickname}</span>
                        <span className={hasAnswered ? 'text-green-400' : 'text-yellow-400'}>
                          {hasAnswered ? '✓' : '...'}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Leaderboard View */}
        {status === 'leaderboard' && (
          <div className="max-w-2xl mx-auto">
            <h2 className="text-3xl font-bold text-white text-center mb-8">Leaderboard</h2>
            <Leaderboard entries={leaderboard} maxEntries={10} showMovement />
            <div className="mt-8 text-center">
              <button
                onClick={nextQuestion}
                className="px-8 py-4 bg-purple-600 hover:bg-purple-500 text-white font-bold rounded-xl transition-colors"
              >
                {currentQuestionIndex + 1 < totalQuestions ? 'Next Question' : 'See Final Results'}
              </button>
            </div>
          </div>
        )}

        {/* Paused View */}
        {status === 'paused' && (
          <div className="flex flex-col items-center justify-center py-20">
            <h2 className="text-4xl font-bold text-yellow-400 mb-8">Game Paused</h2>
            <button
              onClick={resumeGame}
              className="px-8 py-4 bg-green-600 hover:bg-green-500 text-white font-bold rounded-xl transition-colors"
            >
              Resume Game
            </button>
          </div>
        )}

        {/* Game Ended View */}
        {status === 'ended' && (
          <div className="max-w-2xl mx-auto">
            <h2 className="text-4xl font-bold text-white text-center mb-2">Game Over!</h2>
            <p className="text-purple-300 text-center mb-8">Final Standings</p>
            <Leaderboard entries={leaderboard} maxEntries={10} />
            <div className="mt-8 text-center">
              <button
                onClick={() => onEnd?.()}
                className="px-8 py-4 bg-purple-600 hover:bg-purple-500 text-white font-bold rounded-xl transition-colors"
              >
                Back to Dashboard
              </button>
            </div>
          </div>
        )}
      </main>

      {/* Bottom Control Bar */}
      {status !== 'lobby' && status !== 'ended' && (
        <footer className="fixed bottom-0 left-0 right-0 bg-black/40 backdrop-blur-sm border-t border-white/10">
          <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <span className="text-white font-medium">
                Question {currentQuestionIndex + 1} of {totalQuestions}
              </span>
            </div>

            <div className="flex items-center gap-3">
              {status === 'question' && (
                <button
                  onClick={pauseGame}
                  className="px-4 py-2 bg-yellow-500/20 hover:bg-yellow-500/30 text-yellow-400 rounded-lg transition-colors"
                >
                  Pause
                </button>
              )}
              {status === 'question' && (
                <button
                  onClick={skipQuestion}
                  className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors"
                >
                  Skip
                </button>
              )}
              {status === 'reveal' && (
                <button
                  onClick={showLeaderboard}
                  className="px-4 py-2 bg-purple-500/20 hover:bg-purple-500/30 text-purple-300 rounded-lg transition-colors"
                >
                  Show Leaderboard
                </button>
              )}
              <button
                onClick={handleEndGame}
                className="px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg transition-colors"
              >
                End Game
              </button>
            </div>
          </div>
        </footer>
      )}
    </div>
  );
}

export default HostDashboard;
